/**
 * **La boutique** — ⟦A-11 §4⟧, l'écran qui manquait à la couche argent.
 *
 * « La collab fait *entrer* l'argent, la boutique le fait *sortir*. » Les 17 objets de
 * `content/boutique.json` et l'action `acheter_objet` existaient depuis l'avenant ; il n'y
 * avait simplement aucun endroit où les dépenser. Sans elle, les Clicks du Grand Saut ne
 * servent qu'à payer les charges — c'est-à-dire à ne rien décider.
 *
 * **Ce qu'on achète ici, c'est de quoi VIVRE**, jamais de quoi filmer : l'échelle de matériel
 * de tournage existe déjà et c'est la branche Tournage de l'arbre (webcam → ring light →
 * setup → B-roll → multicam → plateau, en PC + Clicks). Confondre les deux viderait l'arbre.
 *
 * **Aucune règle n'est réécrite ici** : la raison exacte d'un refus vient de `refusAchatObjet`,
 * la possession de `objetPossede`, les prérequis de `prerequisManquantsObjet`, le prix du
 * contenu. L'écran affiche, confirme, et envoie `acheter_objet`.
 *
 * Une seule chose est **signalée et non tranchée** : il n'y a pas de réserve de consommables
 * en v1 (arbitrage du moteur, `engine/boutique.ts`). Acheter une boisson, c'est la boire — ou
 * la mettre au pied du lit pour la nuit. L'écran le dit avant de facturer.
 *
 * ⟦A-60⟧ **Troisième rayon : le matos de la vidéo qui compte.** ⟦A-51⟧ avait mesuré le trou —
 * aucun des 17 objets ne touchait un mini-jeu, la moitié « temporaire et fort » de la grille
 * ⟦A-50 ter⟧ était vide pendant que l'arbre portait 16 aides permanentes. Ces consommables-là
 * ne se boivent pas : ils s'**arment**, pour la prochaine production, et **un seul à la fois**
 * — c'est ce qui les empêche de devenir un second arbre qu'on achèterait en Clicks.
 *
 * ## ⟦C57 · lot 5a · A-174⟧ Moins de texte : une carte, c'est un nom, un prix, un effet, un bouton
 *
 * Mesuré avant le chantier : 139 mots visibles (1 053 en faisant défiler), dont la description
 * littéraire de chaque objet, trois blocs de règles et un libellé de rayon de huit mots. La règle
 * d'A-174 : un titre de 5 mots au plus, les chiffres qui comptent, une phrase de 12 mots au plus par
 * bloc. Donc, par carte : le nom (court), le prix, l'effet en une ligne chiffrée, « Acheter », et le
 * pictogramme 📰 qui ouvre **la description, l'effet complet, quand il agit et ce qu'il faut pour
 * l'avoir**. Les règles de la boutique (« ça ne se boit pas, ça se prépare ») sont dans un seul
 * « Les dessous », en haut. **Rien n'est supprimé, rien n'est réécrit** : `nom_court`,
 * `description_courte` (le survol de la carte) et `effet_court` sont écrits À CÔTÉ des longs, dans
 * `content/boutique.json` ; sans eux l'écran retombe sur le long. La confirmation d'achat tient en
 * deux phrases courtes (ce que ça devient, ce qu'il reste).
 */

import {
  chargesDuJour,
  contenuBoutique,
  inventaire,
  objetPossede,
  prerequisManquantsObjet,
  prochaineProd,
  refusAchatObjet,
  // Le type de **contenu** (`nom`, `description`, `effet_lisible`), pas la vue structurelle
  // minimale du moteur (`BuyableObjet`) : l'écran a besoin des textes, le moteur non. Les
  // fonctions du moteur acceptent l'un pour l'autre — `boutique.ts` le verrouille par
  // assertion de compatibilité.
  type BoutiqueObjet,
  type InvalidAction,
} from "@maxime/shared";

import { IconeAsset } from "../../components/IconeAsset";
import { DessousBloc, DessousLignes, LesDessous } from "../../components/LesDessous";
import { Panneau } from "../../components/Panneau";
import { contenu } from "../../game/content";
import { messageErreur } from "../../game/erreurs";
import { nombre } from "../../game/format";
import { useRunStore } from "../../stores/runStore";
import { dialog } from "../../stores/uiStore";

/** Le titre court d'une carte : `nom_court` quand le nom dépasse la règle, sinon le nom. */
const nomCourt = (o: BoutiqueObjet) => o.nom_court ?? o.nom;

/**
 * ⟦C57 · A-174⟧ Ce que devient l'achat, en UNE phrase courte (la confirmation dit ça, puis ce qu'il
 * restera). Le texte long de chaque cas vit dans les dessous de la carte (`quandCaAgit`).
 */
function consequenceCourte(objet: BoutiqueObjet): string {
  if (objet.categorie === "permanent") return "Tu le gardes toute la run.";
  if (objet.moment === "prochaine_nuit") return "Posé au pied du lit, il agit cette nuit, une fois.";
  if (objet.moment === "prochaine_prod") return "Pour ta prochaine vidéo, un seul matos à la fois.";
  return "Pas de réserve : il agit tout de suite.";
}

/** Quand l'objet agit, dans les mots d'avant le chantier (ils s'ouvrent dans les dessous de la carte). */
function quandCaAgit(objet: BoutiqueObjet): string {
  if (objet.categorie === "permanent")
    return `Tu le gardes pour toute la run : un meuble ne se revend pas, et ${nombre(objet.prix_clicks)} ₵, ça se sent sur les charges.`;
  if (objet.moment === "prochaine_nuit")
    return "Il n'y a pas de réserve : tu le poses au pied du lit, il agira à ta prochaine nuit, une seule fois.";
  if (objet.moment === "prochaine_prod")
    return "Ça sert à ta PROCHAINE vidéo, une seule fois, et tu ne peux en préparer qu'un.";
  return "Il n'y a pas de réserve : acheter, c'est consommer, tout de suite.";
}

/**
 * Le refus d'un achat, en une ligne chiffrée (« 🔒 Dès 10 000 abonnés »). La phrase entière du moteur
 * (`messageErreur`) reste sur le survol du bouton : rien ne disparaît, la carte dit juste l'essentiel.
 */
function refusCourt(refus: InvalidAction, objet: BoutiqueObjet, noms: string[]): string {
  switch (refus.code) {
    case "objet_verrouille":
      return `🔒 Dès ${nombre(objet.palier_min)} abonnés`;
    case "objet_prereq_manquant":
      return `🔒 D'abord : ${noms.join(", ")}`;
    case "not_enough_money":
      return `🔒 Il te manque ${nombre(Number(refus.details.requis ?? 0) - Number(refus.details.disponible ?? 0))} ₵`;
    case "objet_deja_prepare":
      return "🎒 Un matos est déjà prêt";
    default:
      return messageErreur(refus);
  }
}

export function Boutique() {
  const run = useRunStore((s) => s.run);
  const act = useRunStore((s) => s.act);
  const pending = useRunStore((s) => s.pending);

  if (!run) return null;
  const state = run.state;
  const c = contenu();
  const catalogue = contenuBoutique(c);
  const inv = inventaire(state);

  /** Le nom lisible d'un objet — jamais son id brut dans une phrase de joueur. */
  const nomDe = (id: string) => c.parObjet.get(id)?.nom ?? id;

  const trier = (objets: readonly BoutiqueObjet[]) =>
    [...objets].sort((a, b) => a.palier_min - b.palier_min || a.prix_clicks - b.prix_clicks);

  const permanents = trier(c.boutique.filter((o) => o.categorie === "permanent"));
  // ⟦A-60⟧ Le matos armé forme son propre rayon : il n'a ni le même geste (on le prépare, on
  // ne le boit pas) ni le même moment (la prochaine vidéo, pas maintenant).
  const armes = trier(c.boutique.filter((o) => o.moment === "prochaine_prod"));
  const consommables = trier(
    c.boutique.filter((o) => o.categorie === "consommable" && o.moment !== "prochaine_prod"),
  );
  // L'objet armé, lu par son id : `objetPrepare` rend la vue structurelle du moteur, l'écran
  // a besoin des TEXTES du contenu (`nom`, `effet_lisible`) — même partage qu'ailleurs ici.
  const arme = c.parObjet.get(prochaineProd(state)[0] ?? "");

  async function acheter(objet: BoutiqueObjet) {
    // ⟦C57 · A-174⟧ Deux phrases courtes : ce que devient l'achat, ce qu'il restera. L'effet est sur
    // la carte, le détail dans ses dessous.
    const ok = await dialog.confirm({
      title: `Acheter « ${nomCourt(objet)} » ?`,
      body: `${consequenceCourte(objet)} Il te restera ${nombre(state.money - objet.prix_clicks)} ₵.`,
      confirmLabel: `Payer ${nombre(objet.prix_clicks)} ₵`,
      cancelLabel: "Pas maintenant",
    });
    if (ok) await act({ type: "acheter_objet", objet_id: objet.id });
  }

  /**
   * Une carte. Une FONCTION qui rend du JSX, pas un composant déclaré dans le rendu : un composant
   * recréé à chaque rendu se démonte, et le dialogue « Les dessous » ouvert dans une carte se
   * refermerait au premier changement du store.
   */
  const carte = (objet: BoutiqueObjet) => {
    const possede = objet.categorie === "permanent" && objetPossede(state, objet.id);
    const refus = possede ? null : refusAchatObjet(state, objet.id, catalogue);
    const manquants = prerequisManquantsObjet(state, objet);
    const enFile = inv.nuit_prochaine.includes(objet.id);
    const enSac = prochaineProd(state).includes(objet.id);
    return (
      // Le survol garde la lecture : la phrase courte (ou la description entière quand elle est déjà courte).
      <li
        key={objet.id}
        className={`noeud ${possede ? "possede" : refus ? "refuse" : "achetable"}`}
        title={objet.description_courte ?? objet.description}
      >
        <div className="noeud-tete">
          {/* ⟦C15 · A-135 · A-136⟧ 17 des 25 objets ont une icône produite
              (`assets/img-src/materiel/shop-<id>.png`) — les 8 consommables ajoutés depuis
              n'en ont pas : `IconeAsset` replie sur rien, comme avant ce chantier. */}
          <IconeAsset className="noeud-icone" src={`/materiel/shop-${objet.id}.png`} />
          <span className="noeud-nom">{nomCourt(objet)}</span>
          <span className="noeud-tier">{nombre(objet.prix_clicks)} ₵</span>
          {/* ⟦C57 · A-174⟧ Tout ce que la carte ne dit plus : le nom entier, la description, l'effet complet. */}
          <LesDessous titre={objet.nom} variante="icone">
            <DessousBloc>
              <p className="dessous-texte">{objet.description}</p>
            </DessousBloc>
            <DessousBloc titre="Ce que ça fait">
              <DessousLignes
                lignes={[
                  { icone: "✨", texte: objet.effet_lisible },
                  { icone: "⏱", texte: quandCaAgit(objet) },
                ]}
              />
            </DessousBloc>
            <DessousBloc titre="Pour l'avoir">
              <DessousLignes
                lignes={[
                  { icone: "💰", texte: `${nombre(objet.prix_clicks)} ₵` },
                  {
                    icone: "🔓",
                    texte: `Ouvert dès ${nombre(objet.palier_min)} abonnés.`,
                  },
                  ...(objet.prerequis.length > 0
                    ? [{ icone: "🔗", texte: `Il te faut d'abord : ${objet.prerequis.map(nomDe).join(", ")}.` }]
                    : []),
                ]}
              />
            </DessousBloc>
          </LesDessous>
        </div>
        <p className="noeud-effet">{objet.effet_court ?? objet.effet_lisible}</p>
        <div className="noeud-pied">
          {objet.categorie === "consommable" && objet.moment === "prochaine_nuit" ? (
            <span className="noeud-cout">🌙 prochaine nuit</span>
          ) : null}
          {possede ? (
            <span className="noeud-etat">chez toi ✔</span>
          ) : (
            <button
              className="bouton"
              type="button"
              disabled={pending || refus !== null}
              title={refus ? messageErreur(refus) : undefined}
              onClick={() => void acheter(objet)}
            >
              Acheter
            </button>
          )}
        </div>
        {!possede && refus !== null ? (
          <p className="noeud-refus">{refusCourt(refus, objet, manquants.map(nomDe))}</p>
        ) : null}
        {enFile ? <p className="noeud-refus">Posé au pied du lit — il agira cette nuit.</p> : null}
        {enSac ? <p className="noeud-refus">Dans le sac — il servira à la prochaine vidéo.</p> : null}
      </li>
    );
  };

  return (
    <Panneau titre={`🛒 Boutique — ${nombre(state.money)} ₵`} large>
      {/* ⟦C57 · A-174⟧ Les deux chiffres qui comptent, et un seul « Les dessous » pour toutes les règles. */}
      <p className="ligne-info l5-entete">
        <span>
          Charges du jour : <strong>{nombre(chargesDuJour(state))} ₵</strong> ·{" "}
          {permanents.filter((o) => objetPossede(state, o.id)).length}/{permanents.length} meuble(s)
        </span>
        <LesDessous titre="Comment marche la boutique">
          <DessousBloc>
            <p className="dessous-texte">
              Les collabs font <strong>entrer</strong> les Clicks, la boutique les fait{" "}
              <strong>sortir</strong>. Ici on n'achète rien pour filmer — le matériel de tournage,
              c'est la branche Tournage de l'arbre. Ici, on achète de quoi{" "}
              <strong>tenir le rythme</strong> : dormir mieux, avoir moins mal au dos, encaisser les
              commentaires.
            </p>
          </DessousBloc>
          <DessousBloc titre="🛋 Les meubles : une fois, pour toute la run">
            <p className="dessous-texte">
              Un meuble se garde, il ne se revend pas : son prix se sent sur les charges.
            </p>
          </DessousBloc>
          <DessousBloc titre="🥤 Les consommables : un coup de fouet, pas une habitude">
            <p className="dessous-texte">
              Pas de réserve en v1 : acheter, c'est consommer. Ce qui agit la nuit se met en file et
              se déclenche au prochain sommeil.
            </p>
          </DessousBloc>
          <DessousBloc titre="🎬 Le matos : pour la vidéo qui compte">
            <p className="dessous-texte">
              Ça ne se boit pas, ça se <strong>prépare</strong> : l'aide sert à ta{" "}
              <strong>prochaine vidéo</strong>, une seule fois, et fort. L'arbre, lui, aide un peu
              moins mais <strong>tous les jours et gratuitement</strong> — le matos ne le remplace
              pas, il te sauve un tournage. <strong>Un seul à la fois</strong>, et il te coûte des
              jauges en plus des Clicks : préparer, ça fatigue.
            </p>
          </DessousBloc>
        </LesDessous>
      </p>

      <section className="carte">
        <div className="boutique-rayon-tete">
          <h2 className="bloc-titre">🛋 Les meubles</h2>
          <span className="boutique-etiquette">à vie</span>
        </div>
        <ul className="noeuds">{permanents.map((objet) => carte(objet))}</ul>
      </section>

      <section className="carte">
        <div className="boutique-rayon-tete">
          <h2 className="bloc-titre">🥤 Les consommables</h2>
          <span className="boutique-etiquette">coup de fouet</span>
        </div>
        <ul className="noeuds">{consommables.map((objet) => carte(objet))}</ul>
      </section>

      <section className="carte">
        <div className="boutique-rayon-tete">
          <h2 className="bloc-titre">🎬 Le matos</h2>
          <span className="boutique-etiquette">prochaine vidéo</span>
        </div>
        {arme ? (
          <p className="ligne-info">
            🎒 Dans le sac : <strong>{nomCourt(arme)}</strong>
          </p>
        ) : null}
        <ul className="noeuds">{armes.map((objet) => carte(objet))}</ul>
      </section>
    </Panneau>
  );
}
